import { useAbortController } from '@packages/core/http';
import { getLogger } from '@packages/core/logging';
import { type DtcCodeEntity, SearchDtcCodes } from '@packages/domain/dtc-codes';
import { filterOutExistingDtcCodes } from '@packages/domain/techline/dtc-codes';
import { container } from '@web/services/container';
import { useCallback, useState } from 'react';

const logger = getLogger('techline');

const [$searchDtcCodes] = container.resolveOperations(SearchDtcCodes);

/**
 * Parameters for the {@link useDtcCodeSearch} hook.
 *
 * @param existingDtcCodes - DTC codes already associated with the case, used to filter duplicates from results.
 * @param skipFiltering - When true, bypasses duplicate filtering and returns all search results.
 */
type UseDtcCodeSearchParams = {
    /** DTC codes already associated with the case, used to filter duplicates from results. */
    existingDtcCodes?: DtcCodeEntity[];

    /** When true, bypasses duplicate filtering and returns all search results. */
    skipFiltering?: boolean;
};

/**
 * Return type for the {@link useDtcCodeSearch} hook.
 */
type UseDtcCodeSearchResult = {
    /** Executes a DTC code search and updates the results state. */
    handleSearch: (query: string) => Promise<void>;

    /** Whether a search request is currently in flight. */
    isSearching: boolean;

    /** The current list of DTC code search results, filtered to exclude existing codes (unless skipped). */
    searchResults: DtcCodeEntity[];
};

/**
 * Manages DTC code search state, including fetching results from the API, filtering out codes that are already
 * associated with the case, and tracking loading state. Aborts in-flight requests when a new search is initiated.
 *
 * @param params - Search configuration including existing codes and filtering preferences.
 * @returns State and a handler for executing DTC code searches.
 */
export function useDtcCodeSearch({
    existingDtcCodes,
    skipFiltering = false,
}: UseDtcCodeSearchParams): UseDtcCodeSearchResult {
    // State
    const [isSearching, setIsSearching] = useState(false);
    const [searchResults, setSearchResults] = useState<DtcCodeEntity[]>([]);

    // Hooks
    const { abortSignalRef, signalAbort } = useAbortController();

    // Handlers
    const handleSearch = useCallback(
        async (query: string) => {
            signalAbort();
            try {
                setIsSearching(true);
                const results = await $searchDtcCodes({ query, signal: abortSignalRef.current });
                const filteredResults = skipFiltering
                    ? results
                    : filterOutExistingDtcCodes(results, existingDtcCodes ?? []);
                setSearchResults(filteredResults);
            } catch (error) {
                logger.error('Failed to search DTC codes', { error });
                setSearchResults([]);
            } finally {
                setIsSearching(false);
            }
        },
        [abortSignalRef, existingDtcCodes, signalAbort, skipFiltering]
    );

    return {
        handleSearch,
        isSearching,
        searchResults,
    };
}
